/**
 * Canonical quota-state mapper — the ONE place quota conditions are detected.
 *
 * This mapper covers two related walls that share one gate component:
 *   - the usage/quota wall (managed-key cap or global kill-switch), and
 *   - an invalid/revoked BYO key (`kind: "byo_key_invalid"`).
 *
 * Each reaches the UI through two transports that cannot be merged:
 *   - synchronous flows: an HTTP 402 with `{ error: { code: "QUOTA_EXCEEDED" |
 *     "BYO_KEY_INVALID", details?: { spent, cap, source } } }`
 *   - polled background tasks (onboarding extraction, CV analysis/assembly): a
 *     session summary carrying `error_reason: "quota_exceeded" |
 *     "byo_key_invalid"` and, for quota, the same `details`.
 *
 * Single source of truth: no component may branch on `error.code` or
 * `error_reason` on its own. Every surface calls one of the `quotaGateFrom*`
 * adapters below, gets back a `QuotaGateState | null`, reads `.kind` to pick the
 * copy, and renders <QuotaGate>. New transports add an adapter here — never a
 * check in a component.
 */

import { ApiRequestError } from './api'

export type QuotaGateKind = 'quota' | 'byo_key_invalid'

export interface QuotaGateState {
  /** Which wall this is — drives the gate's heading, body, and CTAs. */
  kind: QuotaGateKind
  /** USD spent this month; null when the transport carries no figure (billing/key errors). */
  spent: number | null
  /** USD cap; null when unknown. */
  cap: number | null
  /** "billing" (per-user cap) or "global_budget" (all-users kill-switch); "byo" for key errors. */
  source: string
}

interface QuotaDetails {
  spent?: number | null
  cap?: number | null
  source?: string
}

function fromDetails(details: QuotaDetails | null | undefined): QuotaGateState {
  const d = details ?? {}
  return {
    kind: 'quota',
    spent: typeof d.spent === 'number' ? d.spent : null,
    cap: typeof d.cap === 'number' ? d.cap : null,
    source: d.source ?? 'billing',
  }
}

const BYO_KEY_INVALID_GATE: QuotaGateState = {
  kind: 'byo_key_invalid',
  spent: null,
  cap: null,
  source: 'byo',
}

/** Synchronous transport: map a thrown API error to a gate, or null. */
export function quotaGateFromError(err: unknown): QuotaGateState | null {
  if (err instanceof ApiRequestError) {
    if (err.error.code === 'QUOTA_EXCEEDED') {
      return fromDetails(err.error.details as QuotaDetails | undefined)
    }
    if (err.error.code === 'BYO_KEY_INVALID') {
      return BYO_KEY_INVALID_GATE
    }
  }
  return null
}

/** Quota-status transport: GET /api/v1/user/quota → gate when exceeded, else null. */
export function quotaGateFromStatus(status: {
  exceeded: boolean
  spent_usd: number
  cap_usd: number
}): QuotaGateState | null {
  if (!status.exceeded) return null
  return { kind: 'quota', spent: status.spent_usd, cap: status.cap_usd, source: 'billing' }
}

/**
 * Polled-failure transport: a background task that failed carries an
 * `error_reason` (+ `details` for quota). Shared by onboarding extraction and CV
 * analysis/assembly status, which use the same shape.
 */
export function quotaGateFromReason(
  failure: { error_reason?: string | null; details?: QuotaDetails | null } | null | undefined,
): QuotaGateState | null {
  if (!failure) return null
  if (failure.error_reason === 'quota_exceeded') {
    return fromDetails(failure.details)
  }
  if (failure.error_reason === 'byo_key_invalid') {
    return BYO_KEY_INVALID_GATE
  }
  return null
}

/** Onboarding polled transport: extraction summary → gate when quota-terminated, else null. */
export function quotaGateFromExtraction(
  extraction: { error_reason?: string | null; details?: QuotaDetails | null } | null | undefined,
): QuotaGateState | null {
  return quotaGateFromReason(extraction)
}
